<template>
    <div>
        <Head title="Calendrier des interventions" />
        <div class="bg-white rounded-md shadow overflow-hidden p-6">
            <div class="flex justify-between items-center mb-6">
                <div>
                    <h2 class="text-xl font-bold text-gray-800">Calendrier des interventions</h2>
                    <p class="text-sm text-gray-500 mt-1">Planification des demandes de travaux — Service Patrimoine UFHB</p>
                </div>
                <div class="flex items-center gap-3">
                    <!-- Légende -->
                    <div class="flex gap-3 text-xs">
                        <span v-for="s in legendItems" :key="s.slug" class="flex items-center gap-1">
                            <span class="w-3 h-3 rounded-full inline-block" :style="{background: s.color}"></span>
                            {{ s.label }}
                        </span>
                    </div>
                    <Link :href="route('tickets.create')" class="btn-indigo text-sm">+ Nouvelle demande</Link>
                </div>
            </div>

            <!-- Filtres -->
            <div class="flex flex-wrap gap-3 mb-5 pb-4 border-b border-gray-100">
                <select v-model="filterDept" class="text-sm border-gray-300 rounded-md px-3 py-1.5 focus:ring-indigo-500">
                    <option value="">Tous les services</option>
                    <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
                <select v-model="filterType" class="text-sm border-gray-300 rounded-md px-3 py-1.5 focus:ring-indigo-500">
                    <option value="">Tous les types</option>
                    <option v-for="t in types" :key="t.id" :value="t.id">{{ t.name }}</option>
                </select>
                <select v-model="filterPriority" class="text-sm border-gray-300 rounded-md px-3 py-1.5 focus:ring-indigo-500">
                    <option value="">Toutes priorités</option>
                    <option v-for="p in priorities" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
            </div>

            <!-- Calendrier -->
            <FullCalendar :options="calendarOptions" />
        </div>

        <!-- Modal détail ticket -->
        <transition name="fade">
            <div v-if="selectedTicket" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" @click.self="selectedTicket = null">
                <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                    <div class="flex justify-between items-start mb-4">
                        <div>
                            <span class="text-xs font-mono text-gray-400">{{ selectedTicket.uid }}</span>
                            <h3 class="text-lg font-bold text-gray-800 mt-1">{{ selectedTicket.subject }}</h3>
                        </div>
                        <button @click="selectedTicket = null" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                    </div>
                    <div class="space-y-2 text-sm">
                        <div class="flex justify-between">
                            <span class="text-gray-500">Statut</span>
                            <span class="font-medium" :style="{color: selectedTicket.statusColor}">{{ selectedTicket.statusName }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-gray-500">Priorité</span>
                            <span class="font-medium">{{ selectedTicket.priorityName }}</span>
                        </div>
                        <div class="flex justify-between" v-if="selectedTicket.location">
                            <span class="text-gray-500">Localisation</span>
                            <span class="font-medium">{{ selectedTicket.location }}</span>
                        </div>
                        <div class="flex justify-between" v-if="selectedTicket.assignedTo">
                            <span class="text-gray-500">Technicien</span>
                            <span class="font-medium">{{ selectedTicket.assignedTo }}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-gray-500">Ouvert le</span>
                            <span>{{ selectedTicket.openDate }}</span>
                        </div>
                        <div class="flex justify-between" v-if="selectedTicket.dueDate">
                            <span class="text-gray-500">Échéance</span>
                            <span :class="selectedTicket.overdue ? 'text-red-600 font-bold' : ''">{{ selectedTicket.dueDate }}</span>
                        </div>
                    </div>
                    <div class="mt-5 flex gap-3">
                        <Link :href="route('tickets.edit', selectedTicket.uid)" class="btn-indigo text-sm flex-1 text-center">
                            Voir la demande
                        </Link>
                        <a :href="route('tickets.pdf', selectedTicket.id)" target="_blank"
                           class="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                            PDF
                        </a>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
// locale FR inline (pas besoin de @fullcalendar/core/locales/fr)
const frLocale = {
    code: 'fr',
    week: { dow: 1, doy: 4 },
    buttonText: { prev: 'Préc', next: 'Suiv', today: "Aujourd'hui", month: 'Mois', week: 'Semaine', day: 'Jour', list: 'Liste' },
    weekText: 'Sem.',
    allDayText: 'Toute la journée',
    moreLinkText: 'en plus',
    noEventsText: 'Aucun événement à afficher',
}

// Couleurs par statut slug
const STATUS_COLORS = {
    'pending':                  '#f59e0b',
    'waiting_for_confirmation': '#f97316',
    'processing':               '#3b82f6',
    'scheduled':                '#8b5cf6',
    'quote_approved':           '#06b6d4',
    'waiting_for_quote':        '#a78bfa',
    'delay_processing':         '#ef4444',
    'completed':                '#10b981',
    'closed':                   '#6b7280',
}

export default {
    components: { Head, Link, FullCalendar },
    layout: Layout,
    props: {
        tickets:     Array,
        departments: Array,
        types:       Array,
        priorities:  Array,
        auth:        Object,
    },
    data() {
        return {
            selectedTicket: null,
            filterDept:     '',
            filterType:     '',
            filterPriority: '',
            legendItems: [
                { slug: 'waiting_for_confirmation', color: STATUS_COLORS['waiting_for_confirmation'], label: 'En attente validation' },
                { slug: 'processing',               color: STATUS_COLORS['processing'],               label: 'En cours' },
                { slug: 'scheduled',                color: STATUS_COLORS['scheduled'],               label: 'Programmé' },
                { slug: 'completed',                color: STATUS_COLORS['completed'],               label: 'Clôturé' },
                { slug: 'delay_processing',         color: STATUS_COLORS['delay_processing'],        label: 'Retardé' },
            ],
        }
    },
    computed: {
        filteredTickets() {
            return this.tickets.filter(t => {
                if (this.filterDept && t.department_id != this.filterDept) return false
                if (this.filterType && t.type_id != this.filterType) return false
                if (this.filterPriority && t.priority_id != this.filterPriority) return false
                return true
            })
        },
        calendarEvents() {
            return this.filteredTickets.map(t => {
                const color = STATUS_COLORS[t.status_slug] || '#64748b'
                const endDate = t.due || t.open
                return {
                    id:              String(t.id),
                    title:           (t.location ? '[' + t.location + '] ' : '') + t.subject,
                    start:           t.open,
                    end:             endDate,
                    backgroundColor: color,
                    borderColor:     color,
                    textColor:       '#fff',
                    extendedProps:   t,
                }
            })
        },
        calendarOptions() {
            return {
                plugins:      [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView:  'dayGridMonth',
                locale:       frLocale,
                height:       'auto',
                headerToolbar: {
                    left:   'prev,next today',
                    center: 'title',
                    right:  'dayGridMonth,timeGridWeek,timeGridDay',
                },
                buttonText: {
                    today: "Aujourd'hui",
                    month: 'Mois',
                    week:  'Semaine',
                    day:   'Jour',
                },
                events:       this.calendarEvents,
                eventClick:   this.handleEventClick,
                eventMouseEnter: (info) => { info.el.style.cursor = 'pointer' },
                dayMaxEvents: 3,
                nowIndicator: true,
            }
        },
    },
    methods: {
        handleEventClick(info) {
            const t = info.event.extendedProps
            const due = t.due ? new Date(t.due) : null
            this.selectedTicket = {
                id:           t.id,
                uid:          t.uid,
                subject:      t.subject,
                location:     t.location,
                statusName:   t.status_name,
                statusColor:  STATUS_COLORS[t.status_slug] || '#64748b',
                priorityName: t.priority_name,
                assignedTo:   t.assigned_to_name,
                openDate:     new Date(t.open).toLocaleDateString('fr-FR'),
                dueDate:      due ? due.toLocaleDateString('fr-FR') : null,
                overdue:      due && due < new Date() && !['completed','closed'].includes(t.status_slug),
            }
        },
    },
}
</script>

<style>
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to       { opacity: 0; }
.fc .fc-toolbar-title                  { font-size: 1rem; font-weight: 700; }
.fc .fc-button-primary                 { background: #1a3a6b !important; border-color: #1a3a6b !important; }
.fc .fc-button-primary:hover           { background: #162f58 !important; }
.fc .fc-day-today                      { background: #eff6ff !important; }
</style>
